import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Search, X } from "lucide-react";

import logo from "../assets/logo.svg";
import Dashboard from "../assets/instructor/dashboard.svg";
import Courses from "../assets/instructor/courses.svg";
import Quiz from "../assets/instructor/quiz.svg";
import Sessions from "../assets/instructor/sessions.svg";
import Availability from "../assets/instructor/availability.svg";
import Profile from "../assets/instructor/profile.svg";
import Logout from "../assets/instructor/logout.svg";
import LogoutModal from "../components/instructor/LogoutModal";

const sidebarLinks = [
  { to: "/instructor/dashboard", label: "Dashboard", icon: Dashboard },
  { to: "/instructor/courses", label: "My Courses", icon: Courses },
  { to: "/instructor/instructor-quiz", label: "Manage Quiz", icon: Quiz },
  {
    to: "/instructor/manage-sessions",
    label: "Manage Sessions",
    icon: Sessions,
  },
  {
    to: "/instructor/set-availability",
    label: "Availability",
    icon: Availability,
  },
  { to: "/instructor/profile", label: "My Profile", icon: Profile },
  { to: "", label: "Logout", icon: Logout },
];

const InstructorLayout = ({ children }) => {
  const location = useLocation();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  let loggedInUser = null;
  try {
    loggedInUser = JSON.parse(localStorage.getItem("loggedInUser") || "null");
  } catch {
    loggedInUser = null;
  }

  const isActiveLink = (link) => {
    if (link.to === "/instructor/manage-sessions") {
      return location.pathname.startsWith(link.to);
    }
    if (link.to === "/instructor/courses") {
      return location.pathname.startsWith(link.to);
    }

    return location.pathname === link.to;
  };

  const openLogoutModal = () => {
    setMobileSidebarOpen(false);
    setShowLogoutModal(true);
  };

  const closeMobileSidebar = () => setMobileSidebarOpen(false);

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileSidebarOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileSidebarOpen]);

  const renderNavLinks = ({ showLabelsAlways = false, onNavigate } = {}) =>
    sidebarLinks.map((link) =>
      link.label === "Logout" ? (
        <button
          key={link.label}
          type="button"
          onClick={openLogoutModal}
          className="flex w-full items-center gap-3 border border-[#089BC470] px-5 py-4 text-left text-lg font-medium tracking-[6%] text-[#25252B] transition-colors hover:bg-[#E5F6FB] min-[500px]:justify-center min-[500px]:px-4 lg:justify-start lg:gap-4 lg:px-8 2xl:gap-4 2xl:px-11 2xl:py-[19px] 2xl:text-xl"
        >
          <img src={link.icon} alt="" className="shrink-0" />
          <span
            className={
              showLabelsAlways ? "block" : "hidden lg:block"
            }
          >
            {link.label}
          </span>
        </button>
      ) : (
        <Link
          key={link.label}
          to={link.to}
          onClick={onNavigate}
          className={`flex items-center gap-3 border border-[#089BC470] px-5 py-4 tracking-[6%] transition-colors hover:bg-[#E5F6FB] min-[500px]:justify-center min-[500px]:px-4 lg:justify-start lg:gap-4 lg:px-8 2xl:gap-4 2xl:px-11 2xl:py-[19px] ${
            isActiveLink(link)
              ? "bg-[#08B4E4] text-xl font-semibold text-white 2xl:text-[22px]"
              : "text-lg font-medium text-[#25252B] 2xl:text-xl"
          }`}
        >
          <img
            src={link.icon}
            alt=""
            className={`shrink-0 ${
              isActiveLink(link) ? "brightness-0 invert" : ""
            }`}
          />
          <span
            className={
              showLabelsAlways ? "block" : "hidden lg:block"
            }
          >
            {link.label}
          </span>
        </Link>
      ),
    );

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#D9D9D914]">
      <header className="sticky top-0 z-50 flex w-full max-w-[1920px] items-center justify-between border-b border-[#938A96] bg-white px-4 py-3 md:px-8 2xl:px-12 2xl:py-5">
        <div className="flex items-center gap-2 whitespace-nowrap 2xl:gap-3">
          <button
            type="button"
            onClick={() => setMobileSidebarOpen(true)}
            className="mr-1 inline-flex items-center justify-center rounded-md p-1.5 text-[#4A4A68] transition-colors hover:bg-[#F3F4F6] max-[500px]:inline-flex min-[500px]:hidden"
            aria-label="Open sidebar menu"
          >
            <Menu size={24} />
          </button>

          <img
            src={logo}
            alt="STEAM Minds"
            className="h-9 w-14 2xl:h-[49px] 2xl:w-[72px]"
          />

          <span className="text-lg font-semibold tracking-[5%] text-[#4A4A68] 2xl:text-xl 2xl:tracking-[6%]">
            STEAM Minds
          </span>
        </div>

        <div className="flex w-full max-w-[925px] items-center justify-end gap-4 xl:justify-between">
          <div className="relative hidden w-[300px] sm:block xl:w-[425px]">
            <input
              type="text"
              placeholder="Search here"
              className="w-full rounded-full border border-[#938A96] px-6 py-2 text-sm tracking-[6%] focus:border-[#A259F7] focus:outline-none 2xl:px-10 2xl:py-[11px]"
            />

            <Search
              size={18}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#923D8C]"
            />
          </div>

          <div className="flex shrink-0 items-center justify-end gap-3 md:w-[240px]">
            <img
              src={loggedInUser?.profilePicture}
              alt="Instructor"
              className="h-10 w-10 rounded-full object-cover"
            />

            <div className="hidden min-[400px]:flex flex-col">
              <span className="text-sm font-medium leading-[26px] text-[#000]">
                {`${loggedInUser?.firstName || ""} ${loggedInUser?.lastName || ""}`.trim() ||
                  "Instructor"}
              </span>

              <span className="font-['Inter'] text-xs leading-5 text-[#6B7280]">
                Instructor
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop / tablet rail: hidden below 500px; icons only until lg; labels from lg */}
        <aside className="sticky top-0 hidden h-full overflow-y-auto bg-[#f8f8f8] shadow-[2px_0_8.6px_0_#00000054] min-[500px]:block min-[500px]:w-[72px] lg:w-72 2xl:w-[344px]">
          <nav className="bg-white">{renderNavLinks()}</nav>
        </aside>

        {/* Mobile overlay + left drawer */}
        <div
          className={`fixed inset-0 z-[60] bg-black/30 transition-opacity duration-300 max-[500px]:block min-[500px]:hidden ${
            mobileSidebarOpen
              ? "opacity-100"
              : "pointer-events-none opacity-0"
          }`}
          onClick={closeMobileSidebar}
          aria-hidden={!mobileSidebarOpen}
        />

        <aside
          className={`fixed left-0 top-0 z-[70] flex h-screen w-full max-w-[300px] flex-col bg-white shadow-[8px_0_24px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-in-out max-[500px]:flex min-[500px]:hidden ${
            mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          aria-hidden={!mobileSidebarOpen}
        >
          <div className="flex items-center justify-between border-b border-[#E9EAF0] px-4 py-4">
            <div className="flex items-center gap-2">
              <img src={logo} alt="STEAM Minds" className="h-9 w-14" />
              <span className="text-base font-semibold tracking-[5%] text-[#4A4A68]">
                STEAM Minds
              </span>
            </div>
            <button
              type="button"
              onClick={closeMobileSidebar}
              className="rounded-full p-1.5 text-[#1D2026] transition-colors hover:bg-[#F5F5F5]"
              aria-label="Close sidebar menu"
            >
              <X size={22} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto bg-white">
            {renderNavLinks({
              showLabelsAlways: true,
              onNavigate: closeMobileSidebar,
            })}
          </nav>
        </aside>

        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>

      {showLogoutModal ? (
        <LogoutModal setShowLogoutModal={setShowLogoutModal} />
      ) : null}
    </div>
  );
};

export default InstructorLayout;
